import { pdf } from "@react-pdf/renderer";
import { ReactElement } from "react";

/**
 * Convert React PDF component to PDF file (Blob)
 * @param element React PDF component
 * @returns Promise<Blob> PDF file as Blob
 */
export const generatePdfBlob = async (element: ReactElement): Promise<Blob> => {
  try {
    const blob = await pdf(element).toBlob();
    return blob;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error generating PDF blob:", error);
    throw new Error(
      `Failed to generate PDF file: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};

/**
 * Convert React PDF component to PDF file (File object) and automatically download
 * @param element React PDF component
 * @param filename PDF filename
 * @param autoDownload Whether to automatically download the file (default: true)
 * @returns Promise<File> PDF file as File object
 */
export const generatePdfFile = async (
  element: ReactElement,
  filename: string,
  autoDownload: boolean = false,
): Promise<File> => {
  try {
    const blob = await generatePdfBlob(element);
    const file = new File([blob], filename, { type: "application/pdf" });

    // Auto download if enabled
    if (autoDownload) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    return file;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error generating PDF file:", error);
    throw new Error("Failed to generate PDF file");
  }
};

/**
 * Generate PDF filename based on quotation
 * @param quoteNo Quotation number
 * @param customerName Customer name
 * @returns string PDF filename
 */
export const generatePdfFilename = (quoteNo: string, customerName: string): string => {
  const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  const sanitizedName = customerName.replace(/[^a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF]/g, "_");
  return `quotation_${quoteNo}_${sanitizedName}_${timestamp}.pdf`;
};

/**
 * Download PDF file from blob or file
 * @param pdfData PDF data as Blob or File
 * @param filename Filename for download
 */
export const downloadPdfFile = (pdfData: Blob | File, filename: string): void => {
  const url = URL.createObjectURL(pdfData);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
